// Pure access-control decisions for co-producers.
//
// A co-producer is a user who was granted access to ONE production (briefing)
// via a secret link + password. They have full editing rights INSIDE that
// production but must never reach a layer above it: no overview of all
// productions (/dashboard), no /settings, no /my-team, and no other
// production. Keeping the decision here (no React imports) lets the route
// guards stay thin and lets us unit-test every branch — same pattern as
// productionNavigation.ts.

import { productionOverviewPath } from "./productionNavigation";

/** Minimum password length the owner must set for a co-producer (mirrors the
 *  create-co-producer edge function's own check). */
export const CO_PRODUCER_MIN_PASSWORD_LENGTH = 8;

/** Domain of the hidden Supabase Auth accounts backing co-producers (mirrors
 *  CO_PRODUCER_EMAIL_DOMAIN in supabase/functions/_shared/coProducer.ts). */
export const CO_PRODUCER_EMAIL_DOMAIN = "coproducer.produce.app";

/** True for a hidden co-producer account's synthetic email — used to keep these
 *  accounts out of the admin user list. */
export function isCoProducerEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith(`@${CO_PRODUCER_EMAIL_DOMAIN}`);
}

/**
 * Rebuild the co-producer access link from a stored token, so the owner can
 * re-show it later (the password is hashed and cannot be re-shown, but the
 * link is reconstructable). Same format as the create-co-producer function.
 */
export function buildCoProducerLink(
  token: string,
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
): string {
  return `${origin.replace(/\/+$/, "")}/co-producer/access?token=${encodeURIComponent(token)}`;
}

export interface ProductionAccessInput {
  /** Auth/roles/subscription still resolving. */
  isLoading: boolean;
  /** There is a logged-in session. */
  hasSession: boolean;
  /** Platform admin — full access. */
  isAdmin: boolean;
  /** Full producer: producer role + active subscription/manual access. */
  hasProducerAccess: boolean;
  /** Briefing ids this user is an active (non-revoked) co-producer of. */
  coProducerBriefingIds: string[];
  /** The production being opened (route param), if any. */
  productionId: string | undefined;
}

export type AccessDecision =
  | { type: "loading" }
  | { type: "allow" }
  | { type: "redirect"; to: string };

/** Home path for a co-producer: the overview of their assigned production. */
export function coProducerHomePath(coProducerBriefingIds: string[]): string | null {
  return coProducerBriefingIds.length > 0 ? productionOverviewPath(coProducerBriefingIds[0]) : null;
}

/**
 * Decide access to a single production-scoped route (/productions/:id/*).
 *
 * Owners/admins keep their existing access (RLS still scopes them to the
 * briefings they actually own). A co-producer is allowed only for a production
 * in their list; anywhere else they are bounced to their own production. A
 * user who is neither falls through to the existing /upgrade redirect.
 */
export function decideProductionAccess(input: ProductionAccessInput): AccessDecision {
  if (input.isLoading) return { type: "loading" };
  if (!input.hasSession) return { type: "redirect", to: "/producer-login" };

  if (input.isAdmin || input.hasProducerAccess) return { type: "allow" };

  if (input.productionId && input.coProducerBriefingIds.includes(input.productionId)) {
    return { type: "allow" };
  }

  const home = coProducerHomePath(input.coProducerBriefingIds);
  if (home) return { type: "redirect", to: home };

  return { type: "redirect", to: "/upgrade" };
}

/**
 * For the global producer routes (/dashboard, /settings, /my-team): a
 * co-producer-only user must be redirected back down to their production
 * instead of the /upgrade page. Returns the redirect path, or null to let the
 * normal producer logic run.
 */
export function decideGlobalProducerRedirect(input: {
  isAdmin: boolean;
  hasProducerAccess: boolean;
  coProducerBriefingIds: string[];
}): string | null {
  if (input.isAdmin || input.hasProducerAccess) return null;
  return coProducerHomePath(input.coProducerBriefingIds);
}
